import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Database, Pencil, Trash2, Plus, Search, ImageIcon, X, Loader2, RefreshCw } from "lucide-react";
import Btn from "../components/Btn";
import api, { apiErr } from "../lib/api";
import { QStimulus, OptMedia, hasMedia } from "../components/QMedia";

const EXAMS = ["", "CPNS", "SNBT"];
const DIFF_LABEL = { "": "Semua Kesulitan", hots: "HOTS", hard: "Sulit", medium: "Sedang", easy: "Mudah" };
const DIFF_BADGE = {
  hots: "bg-su-danger/15 text-su-danger", hard: "bg-su-energy/15 text-su-energy",
  medium: "bg-su-primary/15 text-su-primary", easy: "bg-su-success/15 text-su-success",
};

const blankQ = () => ({
  exam: "CPNS", category: "TIU", subcategory: "Deret Angka", difficulty: "medium",
  question_text: "", question_svg: "", question_image: "",
  options: [{ key: "A", text: "" }, { key: "B", text: "" }, { key: "C", text: "" }, { key: "D", text: "" }],
  correct_answer: "A", explanation: "",
});

export default function AdminQuestions() {
  const [tree, setTree] = useState(null);
  const [filter, setFilter] = useState({ exam: "", category: "", subcategory: "", difficulty: "", q: "" });
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null); // question object or null

  const loadTree = () => api.get("/admin/questions/tree").then(({ data }) => setTree(data)).catch(() => setTree({ tree: {}, total: 0 }));
  const loadList = () => {
    setRows(null);
    const p = new URLSearchParams();
    Object.entries(filter).forEach(([k, v]) => v && p.set(k, v));
    api.get(`/admin/questions?${p.toString()}`).then(({ data }) => setRows(data.questions)).catch(() => setRows([]));
  };

  useEffect(() => { loadTree(); }, []);
  useEffect(() => { loadList(); /* eslint-disable-next-line */ }, [filter]);

  const refresh = () => { loadTree(); loadList(); };

  const del = async (qid) => {
    if (!window.confirm("Hapus soal ini? Tindakan tidak bisa dibatalkan.")) return;
    try { await api.delete(`/admin/questions/${qid}`); toast.success("Soal dihapus"); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const categories = tree ? Object.keys(tree.tree[filter.exam]?.categories || (filter.exam ? {} : mergeCats(tree.tree))) : [];
  const subcats = tree && filter.category ? Object.keys(getSubcats(tree.tree, filter.exam, filter.category)) : [];

  return (
    <div className="space-y-4" data-testid="admin-questions">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Database className="w-5 h-5 text-su-primary" />
          <h2 className="font-heading font-black text-lg">Bank Soal {tree && <span className="text-su-muted font-medium text-sm">({tree.total})</span>}</h2>
        </div>
        <div className="flex gap-2">
          <button data-testid="q-refresh" onClick={refresh} className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border-2 border-su-border bg-su-surface font-heading font-extrabold text-sm hover:border-su-primary"><RefreshCw className="w-4 h-4" /> Muat Ulang</button>
          <Btn data-testid="q-add" size="sm" onClick={() => setEditing(blankQ())}><Plus className="w-4 h-4" /> Tambah Soal</Btn>
        </div>
      </div>

      {/* filters */}
      <div className="card-chunky p-3 grid sm:grid-cols-5 gap-2" data-testid="q-filters">
        <FSelect testid="q-filter-exam" value={filter.exam} onChange={(v) => setFilter((f) => ({ ...f, exam: v, category: "", subcategory: "" }))} options={EXAMS} placeholder="Semua Ujian" />
        <FSelect testid="q-filter-category" value={filter.category} onChange={(v) => setFilter((f) => ({ ...f, category: v, subcategory: "" }))} options={["", ...categories]} placeholder="Semua Kategori" />
        <FSelect testid="q-filter-subcategory" value={filter.subcategory} onChange={(v) => setFilter((f) => ({ ...f, subcategory: v }))} options={["", ...subcats]} placeholder="Semua Subtopik" />
        <FSelect testid="q-filter-difficulty" value={filter.difficulty} onChange={(v) => setFilter((f) => ({ ...f, difficulty: v }))} options={["", "hots", "hard", "medium", "easy"]} placeholder="Semua Kesulitan" labels={DIFF_LABEL} />
        <div className="relative">
          <Search className="w-4 h-4 text-su-muted absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="q-filter-search" value={filter.q} onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value }))} placeholder="Cari teks soal..."
            className="w-full pl-9 pr-3 py-2 rounded-xl border-2 border-su-border bg-su-bg text-su-text font-medium text-sm outline-none focus:border-su-primary" />
        </div>
      </div>

      {rows === null ? (
        <div className="min-h-[30vh] grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-su-primary" /></div>
      ) : rows.length === 0 ? (
        <div className="card-chunky p-8 text-center text-su-muted font-medium">Tidak ada soal untuk filter ini.</div>
      ) : (
        <div className="space-y-2">
          {rows.map((q) => (
            <div key={q.qid} className="card-chunky p-4 flex items-start justify-between gap-3" data-testid={`q-row-${q.qid}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-[10px] font-heading font-black uppercase px-2 py-0.5 rounded-md bg-su-primary/10 text-su-primary">{q.exam} · {q.category} · {q.subcategory}</span>
                  <span className={`text-[10px] font-heading font-black uppercase px-2 py-0.5 rounded-md ${DIFF_BADGE[q.difficulty] || DIFF_BADGE.medium}`}>{DIFF_LABEL[q.difficulty] || q.difficulty || "Sedang"}</span>
                  {(q.is_figural || q.question_svg) && <span className="text-[10px] font-heading font-black uppercase px-2 py-0.5 rounded-md bg-su-secondary/15 text-su-secondaryShadow inline-flex items-center gap-1"><ImageIcon className="w-3 h-3" /> Figural</span>}
                  <span className="text-[10px] text-su-muted font-mono">{q.source || "manual"}</span>
                </div>
                <p className="font-medium text-sm truncate">{q.question_text || "(soal gambar)"}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                <button data-testid={`q-edit-${q.qid}`} onClick={() => setEditing(q)} className="w-9 h-9 grid place-items-center rounded-xl border-2 border-su-border hover:border-su-primary text-su-primary"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`q-delete-${q.qid}`} onClick={() => del(q.qid)} className="w-9 h-9 grid place-items-center rounded-xl border-2 border-su-border hover:border-su-danger text-su-danger"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <EditModal initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />}
    </div>
  );
}

function EditModal({ initial, onClose, onSaved }) {
  const [q, setQ] = useState(() => ({
    ...blankQ(), ...initial,
    question_text: initial.question_text || "", question_svg: initial.question_svg || "", question_image: initial.question_image || "",
    explanation: initial.explanation || "",
    options: (initial.options || blankQ().options).map((o) => ({ key: o.key, text: o.text || "", svg: o.svg || "", image: o.image || "" })),
  }));
  const [busy, setBusy] = useState(false);
  const isNew = !initial.qid;

  const setF = (k, v) => setQ((s) => ({ ...s, [k]: v }));
  const setOpt = (i, k, v) => setQ((s) => ({ ...s, options: s.options.map((o, idx) => idx === i ? { ...o, [k]: v } : o) }));
  const addOpt = () => setQ((s) => ({ ...s, options: [...s.options, { key: String.fromCharCode(65 + s.options.length), text: "" }] }));
  const delOpt = (i) => setQ((s) => ({ ...s, options: s.options.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!q.options.length) return toast.error("Minimal 1 opsi jawaban");
    if (!q.options.find((o) => o.key === q.correct_answer)) return toast.error("Jawaban benar harus salah satu key opsi");
    setBusy(true);
    const payload = {
      exam: q.exam, category: q.category, subcategory: q.subcategory, difficulty: q.difficulty || "medium",
      question_text: q.question_text || "", question_image: q.question_image || null, question_svg: q.question_svg || null,
      options: q.options.map((o) => ({ key: o.key, text: o.text || "", svg: o.svg || null, image: o.image || null })),
      correct_answer: q.correct_answer, explanation: q.explanation || "",
    };
    try {
      if (isNew) await api.post("/admin/questions", payload);
      else await api.put(`/admin/questions/${initial.qid}`, payload);
      toast.success(isNew ? "Soal ditambahkan" : "Soal diperbarui");
      onSaved();
    } catch (e) { toast.error(apiErr(e)); setBusy(false); }
  };

  const uploadImg = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (isNew) { toast.error("Simpan soal dulu, lalu upload gambar saat mengedit."); return; }
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/admin/questions/${initial.qid}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF("question_image", data.question_image);
      toast.success("Gambar diunggah");
    } catch (err) { toast.error(apiErr(err)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto" data-testid="q-edit-modal">
      <div className="card-chunky w-full max-w-2xl my-8 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-su-surface border-b-2 border-su-border p-4 flex items-center justify-between z-10">
          <h3 className="font-heading font-black text-lg">{isNew ? "Tambah Soal" : "Edit Soal"}</h3>
          <button data-testid="q-modal-close" onClick={onClose} className="text-su-muted hover:text-su-text"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <MField label="Ujian" testid="q-f-exam" value={q.exam} onChange={(v) => setF("exam", v)} />
            <MField label="Kategori" testid="q-f-category" value={q.category} onChange={(v) => setF("category", v)} />
            <MField label="Subtopik" testid="q-f-subcategory" value={q.subcategory} onChange={(v) => setF("subcategory", v)} />
            <label className="block">
              <span className="text-[11px] font-heading font-black uppercase text-su-muted">Kesulitan</span>
              <select data-testid="q-f-difficulty" value={q.difficulty || "medium"} onChange={(e) => setF("difficulty", e.target.value)}
                className="w-full mt-1 px-3 py-2.5 rounded-xl border-2 border-su-border bg-su-bg text-su-text font-medium text-sm outline-none focus:border-su-primary">
                {["hots", "hard", "medium", "easy"].map((o) => <option key={o} value={o}>{DIFF_LABEL[o]}</option>)}
              </select>
            </label>
          </div>

          <MArea label="Teks Soal" testid="q-f-text" value={q.question_text} onChange={(v) => setF("question_text", v)} rows={3} />

          <div>
            <MArea label="Gambar SVG stimulus (opsional — untuk soal figural)" testid="q-f-svg" value={q.question_svg} onChange={(v) => setF("question_svg", v)} rows={4} mono />
            {q.question_svg && <div className="mt-2 p-2 rounded-xl border-2 border-su-border bg-white"><QStimulus q={q} /></div>}
          </div>

          <div className="flex items-end gap-2">
            <MField label="URL Gambar stimulus (opsional)" testid="q-f-image" value={q.question_image || ""} onChange={(v) => setF("question_image", v)} className="flex-1" />
            <label className="shrink-0">
              <span className="sr-only">Upload</span>
              <input type="file" accept="image/*" className="hidden" data-testid="q-f-image-upload" onChange={uploadImg} />
              <span className="inline-flex items-center gap-1 px-3 py-2.5 rounded-xl border-2 border-su-border bg-su-surface font-heading font-extrabold text-sm cursor-pointer hover:border-su-primary"><ImageIcon className="w-4 h-4" /> Upload</span>
            </label>
          </div>

          {/* options */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-heading font-black uppercase text-su-muted">Opsi Jawaban</span>
              <button data-testid="q-add-option" onClick={addOpt} className="text-xs font-heading font-extrabold text-su-primary inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Tambah Opsi</button>
            </div>
            {q.options.map((o, i) => (
              <div key={i} className="rounded-xl border-2 border-su-border p-2 space-y-2" data-testid={`q-option-${i}`}>
                <div className="flex items-center gap-2">
                  <input value={o.key} onChange={(e) => setOpt(i, "key", e.target.value.toUpperCase().slice(0, 2))} className="w-12 text-center px-2 py-2 rounded-lg border-2 border-su-border bg-su-bg font-heading font-black" data-testid={`q-opt-key-${i}`} />
                  <input value={o.text || ""} onChange={(e) => setOpt(i, "text", e.target.value)} placeholder="Teks opsi (kosongkan bila pakai gambar)" className="flex-1 px-3 py-2 rounded-lg border-2 border-su-border bg-su-bg font-medium text-sm" data-testid={`q-opt-text-${i}`} />
                  <label className="flex items-center gap-1 text-xs font-heading font-black cursor-pointer">
                    <input type="radio" name="correct" checked={q.correct_answer === o.key} onChange={() => setF("correct_answer", o.key)} className="accent-[var(--su-success)]" data-testid={`q-opt-correct-${i}`} /> Benar
                  </label>
                  <button onClick={() => delOpt(i)} className="text-su-danger" data-testid={`q-opt-del-${i}`}><Trash2 className="w-4 h-4" /></button>
                </div>
                <textarea value={o.svg || ""} onChange={(e) => setOpt(i, "svg", e.target.value)} placeholder="SVG opsi (opsional, untuk figural)" rows={2} className="w-full px-2 py-1.5 rounded-lg border border-su-border bg-su-bg font-mono text-xs" data-testid={`q-opt-svg-${i}`} />
                {(o.svg || o.image) && <div className="p-1 rounded-lg border border-su-border bg-white inline-flex"><OptMedia opt={o} /></div>}
              </div>
            ))}
          </div>

          <MArea label="Pembahasan" testid="q-f-explanation" value={q.explanation} onChange={(v) => setF("explanation", v)} rows={2} />

          <div className="flex gap-2 pt-2">
            <Btn data-testid="q-modal-save" onClick={save} disabled={busy} className="flex-1">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan"}</Btn>
            <Btn variant="ghost" onClick={onClose}>Batal</Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

function mergeCats(tree) {
  const out = {};
  Object.values(tree).forEach((ex) => Object.assign(out, ex.categories));
  return out;
}
function getSubcats(tree, exam, category) {
  if (exam) return tree[exam]?.categories?.[category]?.subcategories || {};
  const out = {};
  Object.values(tree).forEach((ex) => { const c = ex.categories?.[category]; if (c) Object.assign(out, c.subcategories); });
  return out;
}

function FSelect({ testid, value, onChange, options, placeholder, labels }) {
  return (
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
      className="px-3 py-2 rounded-xl border-2 border-su-border bg-su-bg text-su-text font-medium text-sm outline-none focus:border-su-primary">
      {options.map((o) => <option key={o} value={o}>{(labels && labels[o]) || o || placeholder}</option>)}
    </select>
  );
}
function MField({ label, testid, value, onChange, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[11px] font-heading font-black uppercase text-su-muted">{label}</span>
      <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full mt-1 px-3 py-2.5 rounded-xl border-2 border-su-border bg-su-bg text-su-text font-medium text-sm outline-none focus:border-su-primary" />
    </label>
  );
}
function MArea({ label, testid, value, onChange, rows = 3, mono }) {
  return (
    <label className="block">
      <span className="text-[11px] font-heading font-black uppercase text-su-muted">{label}</span>
      <textarea data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} rows={rows}
        className={`w-full mt-1 px-3 py-2.5 rounded-xl border-2 border-su-border bg-su-bg text-su-text font-medium text-sm outline-none focus:border-su-primary ${mono ? "font-mono text-xs" : ""}`} />
    </label>
  );
}
